import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';
import { SelectedCardType } from '../Types/diagram-type';

interface SidebarNavigationProps {
  onNavigate?: (section: string) => void;
  onBack?: () => void;
  onSave?: () => void;
  onDiscard?: () => void;
  onRegenerate?: () => void;
  saveDisabled?: boolean;
  regenerateDisabled?: boolean;
  regenerateDisabledReason?: string;
  selectedCard?: SelectedCardType | null;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  onNavigate,
  onBack,
  onRegenerate,
  regenerateDisabled,
  regenerateDisabledReason,
  selectedCard,
}) => {
  const [selectedItem, setSelectedItem] = useState<string>('process-diagram');
  const diagramData = useSelector((state: RootState) => state.diagramViewer.diagramData);

  const handleItemClick = (item: string) => {
    setSelectedItem(item);
    onNavigate?.(item);
  };

  // Get creationSource from selectedCard or selectedSubprocess
  const creationSource =
    selectedCard?.creationSource ||
    diagramData?.selectedSubprocess?.creationSource ||
    diagramData?.DiagramData?.creationSource;

  // Check if creationSource is 'ideation' to hide certain menu items
  const isIdeationSource = creationSource === 'ideation';

  const allMenuItems = [
    {
      id: 'process-diagram',
      label: 'Process Diagram',
      icon: <img src="/images/process-icon.svg" alt="icon" style={{ width: 19, height: 19 }} />,
      activeIcon: (
        <img src="/images/process-active-icon.svg" alt="icon" style={{ width: 22, height: 22 }} />
      ),
      primary: true,
    },
    // {
    //   id: 'process-info',
    //   label: 'Process Info',
    //   icon: <img src="/images/info-icon.svg" alt="icon" style={{ width: 18, height: 18 }} />,
    //   activeIcon: (
    //     <img src="/images/info-active-icon.svg" alt="icon" style={{ width: 20, height: 20 }} />
    //   ),
    //   primary: false,
    //   showForIdeation: false, // Hide for ideation source
    // },
    // {
    //   id: 'opportunities',
    //   label: 'Proposed Enhancements',
    //   icon: (
    //     <img src="/images/opportunities-icon.svg" alt="icon" style={{ width: 18, height: 18 }} />
    //   ),
    //   activeIcon: (
    //     <img
    //       src="/images/opportunities-active-icon.svg"
    //       alt="icon"
    //       style={{ width: 21, height: 21 }}
    //     />
    //   ),
    //   primary: false,
    //   showForIdeation: false, // Hide for ideation source
    // },
    {
      id: 'solutions',
      label: 'AI Opportunities',
      icon: <img src="/images/solution-icon.svg" alt="icon" style={{ width: 21, height: 21 }} />,
      activeIcon: (
        <img src="/images/solution-active-icon.svg" alt="icon" style={{ width: 21, height: 21 }} />
      ),
      primary: false,
    },
    // {
    //   id: 'data-sources',
    //   label: 'Data Sources',
    //   icon: (
    //     <img src="/images/data-sources-icon.svg" alt="icon" style={{ width: 17, height: 17 }} />
    //   ),
    //   activeIcon: (
    //     <img
    //       src="/images/data-sources-active-icon.svg"
    //       alt="icon"
    //       style={{ width: 17, height: 17 }}
    //     />
    //   ),
    //   primary: false,
    // },
  ];

  // Filter menu items based on creationSource
  const menuItems = allMenuItems.filter((item) => {
    // Hide 'Process Info' and 'Opportunities' when creationSource is 'ideation'
    if (isIdeationSource && (item.id === 'process-info' || item.id === 'opportunities')) {
      return false;
    }
    return true;
  });

  return (
    <Box
      sx={{
        width: 280,
        mt: 6,
        height: '100vh',
        backgroundColor: 'background.paper',
        borderRight: '1px solid #E0E0E0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Back Button */}
      <Box
        sx={{
          py: 3,
          paddingLeft: 4,
          paddingTop: 5,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          // gap: 1,
          '&:hover': {
            backgroundColor: '#EEEEEE',
          },
        }}
        onClick={onBack}
      >
        <ArrowBackIosIcon sx={{ fontSize: 10, color: '#313131' }} />
        <Box
          sx={{
            fontFamily: 'Poppins',
            fontSize: '14px',
            fontWeight: 400,
            color: '#313131',
            lineHeight: '100%',
            verticalAlign: 'middle',
            ml: 0.5,
          }}
        >
          Back
        </Box>
      </Box>

      {/* Navigation List */}
      <List sx={{ p: 0, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={selectedItem === item.id}
              onClick={() => handleItemClick(item.id)}
              sx={{
                py: 2,
                px: 1,
                backgroundColor: selectedItem === item.id ? '#2962FF' : 'transparent',
                color: selectedItem === item.id ? '#FFF' : '#333',
                '&:hover': {
                  backgroundColor: selectedItem === item.id ? '#1E4FCC' : '#EEEEEE',
                },
                '&.Mui-selected': {
                  backgroundColor: '#2962FF',
                  color: '#FFF',
                  '&:hover': {
                    backgroundColor: '#1E4FCC',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 48, // Increased space to provide more room for icons
                  color: selectedItem === item.id ? '#FFF' : '#333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {selectedItem === item.id ? item.activeIcon : item.icon}
              </ListItemIcon>

              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  sx: {
                    fontFamily: 'Poppins',
                    fontWeight: selectedItem === item.id ? 500 : 400,
                    fontStyle: 'normal',
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: 0,
                    color: 'inherit',
                    verticalAlign: 'middle',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Footer with Dynamic Regenerate Button */}
      <Box
        sx={{
          height: 113,
          p: 2,
          backgroundColor: 'background.paper',
        }}
      >
        {(selectedItem === 'process-diagram' ||
          selectedItem === 'process-info' ||
          selectedItem === 'opportunities') &&
        !isIdeationSource ? (
          <Tooltip
            title={
              regenerateDisabled ||
              (diagramData?.isRegenerating &&
                diagramData?.regeneratingSubprocessId === selectedCard?.id)
                ? regenerateDisabledReason || 'This will be enabled after applicable updates'
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                onClick={onRegenerate}
                disabled={
                  !onRegenerate ||
                  !!regenerateDisabled ||
                  (diagramData?.isRegenerating &&
                    diagramData?.regeneratingSubprocessId === selectedCard?.id)
                }
                fullWidth
                sx={{
                  textTransform: 'none',
                  borderColor: '#2962FF',
                  color: '#2962FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  '&:hover': {
                    borderColor: '#1E4FCC',
                    backgroundColor: '#E3F2FD',
                  },
                  '&.Mui-disabled': {
                    borderColor: '#E0E0E0',
                    color: '#9E9E9E',
                  },
                }}
              >
                {diagramData?.isRegenerating &&
                diagramData?.regeneratingSubprocessId === selectedCard?.id ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    <span>Regenerating...</span>
                  </>
                ) : (
                  <>
                    <img
                      src="/images/regenerate.svg"
                      alt="Regenerate Icon"
                      style={{
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        opacity: onRegenerate && !regenerateDisabled ? 1 : 0.4,
                        filter:
                          onRegenerate && !regenerateDisabled
                            ? 'brightness(0)'
                            : 'brightness(0) grayscale(100%)',
                        transition: 'opacity 0.2s ease',
                      }}
                    />
                    <span>Regenerate AI Opportunities</span>
                  </>
                )}
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
};

export default SidebarNavigation;
